/**
 * Add to Cart Handler
 * Direct conversion from TypeScript master add-to-cart.directive.ts
 */
// @ts-nocheck
import CartManager from './cart-manager.js';
import { SideCartManager } from './side-cart-manager.js';
import { NotificationManager } from './notification-manager.js';
import { CartUpdateEvent } from '@theme/events';
class AddToCartHandler {
  constructor(button) {
    this.button = button;
    this.addingText = button.getAttribute('adding-text') || 'Adding...';
    this.noProps = button.hasAttribute('add-to-cart-no-props');
    this.isSwatch = button.hasAttribute('add-to-cart-swatch');
    this.rugFlowHandle = button.getAttribute('custom-rug-flow-handle');
    this.flow = button.getAttribute('add-to-cart-flow') || 'default';
    this.defaultText = button.textContent;
    this.productForm = button.closest('form');
    this.previousText = null;
    this.cartManager = CartManager.getInstance();
    this.sideCartManager = SideCartManager.getInstance();
    this.queuedOperation = new Map();
    this.isBound = false;
    this.bindEvents();
  }
  getCurrentVariantId() {
    // Priority 1: Button attribute (matches TypeScript @HostAttr behavior)
    const attrId = this.button.getAttribute('add-to-cart');
    if (attrId) {
      return Number(attrId);
    }
    
    // Priority 2: Form input (fallback)
    if (this.productForm) {
      const idInput = this.productForm.querySelector('[name="id"]');
      if (idInput && idInput.value) {
        return Number(idInput.value);
      }
    }
    
    return null;
  }
  bindEvents() {
    // Direct conversion from TypeScript line 53-58
    if (this.isBound) return;
    this.button.addEventListener('click', this.onClick.bind(this));
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.preventFormSubmit.bind(this));
    }
    this.isBound = true;
  }

  preventFormSubmit(e) {
    // Direct conversion from TypeScript line 227
    e.preventDefault();
  }

  async onClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const id = this.getCurrentVariantId();
    if (!id) return;
    if (this.isSwatch) {
      const isAllowed = this.onSwatchAddCallback(id);
      if (!isAllowed) {
        return;
      }
    }

    // Direct conversion from TypeScript line 73-76
    if (window.gsap) {
      window.gsap.set(this.button, { width: this.button.offsetWidth });
    } else {
      this.button.style.width = `${this.button.offsetWidth}px`;
    }
    this.previousText = this.button.textContent;
    this.button.textContent = this.addingText;
    this.button.disabled = true;

    const formData = new FormData(this.productForm || document.createElement('form'));

    // Extract properties (matches TypeScript line 80-116)
    const prodProps = {};
    if (this.productForm && !this.noProps) {
      const quantityInput = this.productForm.querySelector('[name="quantity"]');
      const giftCardRecipientForm = this.productForm.querySelector('recipient-form');

      if (giftCardRecipientForm && !this.productForm.checkValidity()) {
        this.productForm.reportValidity();
        this.resetButton();
        return;
      }

      if (quantityInput && !quantityInput.checkValidity()) {
        quantityInput.reportValidity();
        this.resetButton();
        return;
      }

      const rawQty = formData.get('quantity');
      const quantity = Math.max(1, Number(rawQty || 1));

      if (!this.noProps) {
        Array.from(formData.entries())
          .filter(([key]) => key.startsWith('properties['))
          .forEach(([key, value]) => {
            prodProps[key] = `${value}`;
          });

        // Convert bracket notation to dot notation (matches TypeScript line 107-116)
        Object.keys(prodProps).forEach(key => {
          let path = key.replace(/\[/g, '.').replace(/\]/g, '').split('.');
          let last = path.pop();
          prodProps[last] = prodProps[key];
          delete prodProps[key];
        });
      }
    }

    // Handle product add-ons (matches TypeScript line 119-136)
    const productAddOnElement = this.productForm?.querySelector('[product-add-on]');
    const productAddOnChecked = productAddOnElement?.querySelector('[product-add-on-toggle]:checked');

    if (productAddOnElement && productAddOnChecked) {
      const productAddOnVariantId = productAddOnElement.getAttribute('product-add-on');
      const productAddOnTitle = productAddOnElement.getAttribute('product-add-on-title');
      const productAddOnPrice = productAddOnElement.getAttribute('product-add-on-price');
      const productAddOnMatchQuantity = productAddOnElement.getAttribute('product-add-on-match-quantity');

      if (productAddOnChecked && productAddOnTitle && productAddOnVariantId && productAddOnPrice) {
        Object.assign(prodProps, {
          _add_on_id: productAddOnVariantId,
          _add_on_title: productAddOnTitle,
          _add_on_price: productAddOnPrice,
          _add_on_match_qty: productAddOnMatchQuantity
        });
      }
    }

    // Handle custom rugs flow (matches TypeScript line 138-168)
    if (this.flow === 'custom-rugs' && this.rugFlowHandle) {
      try {
        const customRugsJSON = document.getElementById('CustomRugsJSON');
        if (!customRugsJSON) {
          console.error('CustomRugsJSON not found');
          this.resetButton();
          return;
        }

        const flows = JSON.parse(customRugsJSON.textContent);
        const data = flows.find(flow => flow.handle === this.rugFlowHandle);

        if (!data) {
          console.error('Flow data not found for handle:', this.rugFlowHandle);
          this.resetButton();
          return;
        }

        const products = [data.serging_product, data.rug_pad_product];
        const variants = products.flatMap(p => p.variants || []);

        // Check if rug pad is selected (might be "No Rug Pad" or empty)
        const rugPadId = prodProps['rug pad'] ? +prodProps['rug pad'] : null;
        const finishingId = prodProps['finishing'] ? +prodProps['finishing'] : null;

        const rugPad = rugPadId ? variants.find(v => v.id === rugPadId) : null;
        const finishingProduct = finishingId ? products.find(p => (p.variants || []).some(v => v.id === finishingId)) : null;
        const finishingVariant = finishingId && finishingProduct ? finishingProduct.variants?.find(v => v.id === finishingId) : null;

        // Finishing is required, rug pad is optional
        if (!finishingVariant) {
          console.error('Finishing variant not found or not selected');
          this.resetButton();
          return;
        }

        // Direct conversion from TypeScript line 149-150
        // Get directive instance (in JS, we'll use window.customRugsHandler)
        const directive = window.customRugsHandler || null;
        const chosenArea = directive ? directive.chosenArea : 1;
        const chosenPerimeter = directive ? directive.chosenPerimeter : 1;
        const currentPrice = directive ? directive.currentPrice : 0;

        const _rugBundleId = `${Date.now()}`;
        const properties = this.formatCustomRugProperties(prodProps, data);
        properties['_rugBundleId'] = _rugBundleId;

        // Direct conversion from TypeScript line 154-156
        const rugQuantity = Math.ceil(chosenArea);
        const rugPadQuantity = Math.ceil(chosenArea);
        const finishingQuantity = Math.ceil(chosenPerimeter);

        // Direct conversion from TypeScript line 158-162
        const items = [
          { id, quantity: rugQuantity, properties: { ...properties, _rugBundlePrice: currentPrice } },
        ];

        // Add rug pad and finishing if they exist
        if (rugPad && rugPad.id) {
          items.push({ id: rugPad.id, quantity: rugPadQuantity, properties: { _rugBundleId } });
        }
        if (finishingVariant && finishingVariant.id) {
          items.push({ id: finishingVariant.id, quantity: finishingQuantity, properties: { _rugBundleId } });
        }

        // Direct conversion from TypeScript line 164-165
        this.queuedOperation = this.queuedOperation || new Map();
        this.queuedOperation.set(_rugBundleId, items);
        this.addMany(items)
          .then(() => this.onAddCallback())
          .catch((error) => this.onAddRejected(error));
      } catch (error) {
        console.error('Error handling custom rugs flow:', error);
        this.resetButton();
      }
    } else {
      // Direct conversion from TypeScript line 167
      const rawQty = formData.get('quantity');
      const qty = Math.max(1, Number(rawQty || 1));
      this.cartManager.add(id, qty, prodProps).then(this.onAddCallback.bind(this), this.onAddRejected.bind(this));
    }
  }

  async add(id, quantity = 1, properties = {}) {
    return this.cartManager.add(id, quantity, properties);
  }

  async addMany(items) {
    return this.cartManager.addMany(items, false);
  }

  async onAddCallback(data) {
    this.resetButton();
    const cart = await this.cartManager.get();
    document.dispatchEvent(new CartUpdateEvent({ cart: cart }));
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart: cart },
      bubbles: true
    }));
    this.sideCartManager.open();
  }

  onAddRejected(data) {
    // Direct conversion from TypeScript line 179-214
    console.log(data, "data");
    if (this.queuedOperation && this.queuedOperation.size > 0) {
      this.cartManager.get().then(cart => {
        const uniqueBundleIds = [...new Set(cart.items.map(i => i.properties?._rugBundleId).filter(Boolean))];

        uniqueBundleIds?.forEach(id => {
          const queuedBundles = this.queuedOperation.get(id);
          const queuedBundleItems = queuedBundles ? Object.values(queuedBundles) : [];
          const bundleItems = cart.items.filter(item => item.properties?._rugBundleId === id);

          if (!queuedBundleItems || !bundleItems) return;

          bundleItems.forEach(item => {
            const queuedBundleItemMatch = queuedBundleItems.find(obj => obj.id === item.id);

            if (queuedBundleItemMatch?.quantity !== item.quantity) {
              const itemChanges = [];

              bundleItems.forEach((i) => {
                itemChanges.push({ id: i.key, quantity: 0 });
              });

              this.cartManager.changeMany(itemChanges);
              this.queuedOperation.delete(id);
              return;
            }
          });
        });
      });
    }

    this.resetButton();
  }

  resetButton() {
    if (this.previousText) {
      this.button.textContent = this.previousText;
      this.previousText = null;
    } else {
      this.button.textContent = this.defaultText;
    }
    this.button.disabled = false;
    this.button.removeAttribute('style');
  }

  formatCustomRugProperties(props, flowData) {
    // Matches TypeScript formatCustomRugProperties line 228-269
    const ret = {};

    if (['round', 'octagon'].includes(props['shape'])) {
      const diameterInch = +props['diameter-inches'] || 0;
      const diameterFeet = +props['diameter-feet'] || 0;
      ret['Dimension'] = `${diameterFeet}'${diameterInch > 0 ? `${diameterInch}"` : ''}`;
    } else if (props['shape'] === 'square') {
      const widthInch = +props['width-inches'] || 0;
      const widthFeet = +props['width-feet'] || 0;
      const dimension = `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''}`;
      ret['Dimension'] = `${dimension} x ${dimension}`;
    } else if (props['shape'] === 'rectangle') {
      const widthInch = +props['width-inches'] || 0;
      const widthFeet = +props['width-feet'] || 0;
      const lengthInch = +props['length-inches'] || 0;
      const lengthFeet = +props['length-feet'] || 0;
      ret['Dimension'] = `${widthFeet}'${widthInch > 0 ? `${widthInch}"` : ''} x ${lengthFeet}'${lengthInch > 0 ? `${lengthInch}"` : ''}`;
    }

    for (const key in props) {
      if (['shape', 'color'].includes(key)) {
        ret[this.capitalize(key)] = this.capitalize(props[key]);
      }

      if (['finishing', 'rug pad'].includes(key)) {
        const id = +props[key];
        const products = [flowData.serging_product, flowData.rug_pad_product];
        const product = products.find(p => p.id === id || (p.variants || []).some(v => v.id === id));
        if (product) {
          const variant = (product.variants || []).find(v => v.id === id);
          if (variant) {
            ret[this.capitalize(key)] = variant.title;
          }
        }
      }
    }

    return ret;
  }

  capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
  }

  onSwatchAddCallback(id) {
    // Matches TypeScript onSwatchAddCallback line 271-293 exactly
    const { items } = this.cartManager.getState();
    const swatchItems = items.filter(item => item.product_type?.toLowerCase().includes('swatch'));
    const notificationManager = NotificationManager.getInstance();

    if (items.some(item => item.id === id)) {
      // Matches TypeScript line 275-279 exactly
      // Uses translation keys: upsellSwatches.fabricTitle / upsellSwatches.fabricBody
      // Default values from settings_schema.json:
      //   fabricTitle: "This swatch is already in your cart"
      //   fabricBody: "Sample Swatches are limited to one (1) per fabric."
      notificationManager.notify({
        title: this.getTranslation('upsellSwatches.fabricTitle', 'This swatch is already in your cart'),
        duration: 5,
        message: this.getTranslation('upsellSwatches.fabricBody', 'Sample Swatches are limited to one (1) per fabric.')
      });
      return false;
    }

    if (swatchItems.length >= 5) {
      // Matches TypeScript line 284-288 exactly
      // Uses translation keys: upsellSwatches.limitTitle / upsellSwatches.limitBody
      // Default values from settings_schema.json:
      //   limitTitle: "Max 5 Swatches per Order"
      //   limitBody: "You can not order more than five (5) sample swatches per order."
      notificationManager.notify({
        title: this.getTranslation('upsellSwatches.limitTitle', 'Max 5 Swatches per Order'),
        duration: 5,
        message: this.getTranslation('upsellSwatches.limitBody', 'You can not order more than five (5) sample swatches per order.')
      });
      return false;
    }

    return true;
  }

  /**
   * Get translation value from window.translations (matches TypeScript Translation.ts t() function)
   * Falls back to defaultValue if not found
   */
  getTranslation(path, defaultValue) {
    try {
      if (window.translations && Array.isArray(window.translations)) {
        // Merge all translation objects (matches TypeScript Translation.mergeObjs)
        let merged = {};
        for (let i = 0; i < window.translations.length; i++) {
          merged = { ...merged, ...window.translations[i] };
        }
        // Navigate path (matches TypeScript findIn utility)
        const keys = path.split('.');
        let value = merged;
        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return defaultValue;
          }
        }
        return typeof value === 'string' ? value : defaultValue;
      }
    } catch (e) {
      // Silent fallback
    }
    return defaultValue;
  }
}

// Initialize all add-to-cart buttons on page load
function initializeAddToCartHandlers() {
  const buttons = document.querySelectorAll('button[type="submit"][add-to-cart], button[type="button"][add-to-cart]');
  buttons.forEach(button => {
    // Don't re-initialize if already handled
    if (button.dataset.addToCartInitialized) return;
    button.dataset.addToCartInitialized = 'true';
    new AddToCartHandler(button);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAddToCartHandlers);
} else {
  initializeAddToCartHandlers();
}

// Re-initialize on section load (for Shopify Sections API)
document.addEventListener('shopify:section:load', initializeAddToCartHandlers);

